/**
 * math.ts — Tiny, dependency-free math helpers.
 *
 * Kept Phaser-free so the gameplay math that uses them can be unit-tested in a
 * plain Node environment (no canvas / DOM).
 */

/** Clamp `value` into the inclusive range [min, max]. */
export function clamp(value: number, min: number, max: number): number {
  if (value < min) return min;
  if (value > max) return max;
  return value;
}

/**
 * Deterministic pseudo-random in [0, 1) from two numbers.
 *
 * A stand-in for `Math.random()` where the value must be *stable* — the clay
 * wobble on a building is seeded from its position, so the town looks
 * hand-placed but looks the same hand-placed way on every run. That keeps
 * restarts and screenshot comparisons reproducible, which `Math.random()`
 * would not.
 *
 * Not a good hash in any cryptographic sense; it just has to decorrelate
 * neighbouring inputs, which the fract-of-a-big-sine trick does well enough
 * for scattering a few dozen props.
 */
export function hash2(x: number, y: number): number {
  const n = Math.sin(x * 127.1 + y * 311.7) * 43758.5453;
  return n - Math.floor(n);
}

/**
 * Convert a per-frame smoothing factor into a frame-rate independent one.
 *
 * Naive `a += (b - a) * rate` moves a fixed *fraction per frame*, so the same
 * constant smooths faster at 144fps than at 30fps — camera follow and turning
 * would visibly change with the frame rate. Treating the factor as exponential
 * decay over time fixes that: rates stay tuned as if at `referenceFps`, but the
 * result depends only on elapsed time.
 *
 * @param ratePerFrame the hand-tuned 0..1 factor (as if running at referenceFps)
 * @param dt           elapsed seconds this frame
 */
export function smoothing(ratePerFrame: number, dt: number, referenceFps = 60): number {
  if (ratePerFrame <= 0) return 0;
  if (ratePerFrame >= 1) return 1;
  return 1 - Math.pow(1 - ratePerFrame, dt * referenceFps);
}
